<template>
  <div v-loading="loading" class="hezabat-page hezabat-page--report">
    <header class="hezabat-report-head">
      <div class="report-period" aria-label="Report period filter" @keyup.enter="applyFilters">
        <div class="report-period__presets" role="group" aria-label="Report period">
          <button
            v-for="preset in periodPresetOptions"
            :key="preset.id"
            type="button"
            class="report-period__preset"
            :class="{ 'report-period__preset--active': activePeriodPreset === preset.id }"
            :disabled="loading"
            @click="selectPeriodPreset(preset.id)"
          >
            {{ preset.label }}
          </button>
        </div>

        <div class="report-period__divider" aria-hidden="true" />

        <div
          class="report-period__pickers"
          :class="{ 'report-period__pickers--active': isCustomPeriod }"
        >
          <el-date-picker
            v-model="filterDraft.dateFrom"
            type="date"
            size="small"
            class="report-period__date report-period__date--single"
            value-format="YYYY-MM-DD"
            :placeholder="$t('reportDateFrom')"
            :disabled="loading"
            :clearable="false"
          />
          <el-date-picker
            v-model="filterDraft.dateTo"
            type="date"
            size="small"
            class="report-period__date report-period__date--single"
            value-format="YYYY-MM-DD"
            :placeholder="$t('reportDateTo')"
            :disabled="loading"
            :clearable="false"
          />
        </div>

        <button
          type="button"
          class="report-period__search"
          :disabled="loading"
          @click="applyFilters"
        >
          <i class="fa fa-search" aria-hidden="true" />
          <span>{{ $t("search") }}</span>
        </button>

        <button
          v-if="hasActiveFilters"
          type="button"
          class="report-period__reset"
          :disabled="loading"
          @click="clearFilters"
        >
          {{ $t("reportResetPeriod") }}
        </button>
      </div>
    </header>

    <div class="hezabat-report-main">
      <section class="hezabat-report-row hezabat-report-row--primary" aria-label="Report totals and by product">
        <div class="hezabat-report-metrics">
          <article class="hezabat-metric hezabat-metric--sales">
            <div class="hezabat-metric__icon" aria-hidden="true">
              <i class="fas fa-receipt" />
            </div>
            <div class="hezabat-metric__body">
              <span class="hezabat-metric__label">{{ $t("reportTotalSales") }}</span>
              <strong class="hezabat-metric__value">{{ summary.salesCount }}</strong>
              <span class="hezabat-metric__hint">{{ $t("reportInPeriod") }}</span>
            </div>
          </article>

          <article class="hezabat-metric hezabat-metric--premium">
            <div class="hezabat-metric__icon" aria-hidden="true">
              <i class="fas fa-coins" />
            </div>
            <div class="hezabat-metric__body">
              <span class="hezabat-metric__label">{{ $t("reportTotalPremium") }}</span>
              <strong class="hezabat-metric__value">{{ formatPremium(summary.premiumSum) }}</strong>
              <span class="hezabat-metric__hint">{{ $t("reportInPeriod") }}</span>
            </div>
          </article>

          <article class="hezabat-metric hezabat-metric--insured">
            <div class="hezabat-metric__icon" aria-hidden="true">
              <i class="fas fa-user-shield" />
            </div>
            <div class="hezabat-metric__body">
              <span class="hezabat-metric__label">{{ $t("reportTotalInsured") }}</span>
              <strong class="hezabat-metric__value">{{ summary.insuredCount }}</strong>
              <span class="hezabat-metric__hint">{{ $t("reportInPeriod") }}</span>
            </div>
          </article>

          <article class="hezabat-metric hezabat-metric--products">
            <div class="hezabat-metric__icon" aria-hidden="true">
              <i class="fas fa-box-open" />
            </div>
            <div class="hezabat-metric__body">
              <span class="hezabat-metric__label">{{ $t("reportProductsUsed") }}</span>
              <strong class="hezabat-metric__value">{{ summary.productsUsedCount }}</strong>
              <span class="hezabat-metric__hint">{{ $t("reportInPeriod") }}</span>
            </div>
          </article>
        </div>

        <ReportProductChart :key="`product-${chartRenderKey}`" :by-product="byProduct" />
      </section>

      <section class="hezabat-report-row hezabat-report-row--secondary" aria-label="Report by status">
        <ReportStatusChart :key="`status-${chartRenderKey}`" :by-status="byStatus" />
      </section>
    </div>
  </div>
</template>

<script>
import ReportProductChart from "./ReportProductChart.vue";
import ReportStatusChart from "./ReportStatusChart.vue";
import { loadAgentReportStatistics } from "./reportStatisticsApi.js";
import {
  defaultReportDateRange,
  findMatchingReportPeriodPreset,
  getReportPeriodPresetById,
  normalizeFilterPeriodDates,
  REPORT_PERIOD_PRESET_IDS,
  toDateInputValue,
} from "./reportStats.js";
// Mock dataset kept in reportStatsMock.js for local/dev fallback if needed.

const REPORT_PRESET_I18N = {
  month1: "reportPreset1Month",
  quarter3: "reportPreset3Months",
  halfyear: "reportPreset6Months",
  year1: "reportPreset1Year",
};

export default {
  name: "HezabatCustomersPage",
  components: {
    ReportProductChart,
    ReportStatusChart,
  },
  data() {
    const range = defaultReportDateRange();
    const dateFrom = toDateInputValue(range[0]);
    const dateTo = toDateInputValue(range[1]);
    return {
      loading: false,
      filterDraft: {
        dateFrom,
        dateTo,
      },
      appliedFilters: {
        dateFrom,
        dateTo,
      },
      summary: { salesCount: 0, premiumSum: 0, insuredCount: 0, productsUsedCount: 0 },
      byProduct: [],
      byStatus: [],
      chartRenderKey: 0,
    };
  },
  computed: {
    hasActiveFilters() {
      const defaults = defaultReportDateRange();
      const defFrom = toDateInputValue(defaults[0]);
      const defTo = toDateInputValue(defaults[1]);
      return (
        this.appliedFilters.dateFrom !== defFrom || this.appliedFilters.dateTo !== defTo
      );
    },
    periodPresetOptions() {
      return REPORT_PERIOD_PRESET_IDS.map((id) => ({
        id,
        label: this.$t(REPORT_PRESET_I18N[id]),
      }));
    },
    activePeriodPreset() {
      return findMatchingReportPeriodPreset(
        this.appliedFilters.dateFrom,
        this.appliedFilters.dateTo
      );
    },
    isCustomPeriod() {
      return this.activePeriodPreset === null;
    },
  },
  mounted() {
    this.loadReportData();
  },
  methods: {
    formatPremium(value) {
      const n = Number(value) || 0;
      return `${n.toLocaleString(undefined, { maximumFractionDigits: 0 })} ₼`;
    },
    applyFilters() {
      this.appliedFilters = normalizeFilterPeriodDates(
        this.filterDraft.dateFrom,
        this.filterDraft.dateTo,
        defaultReportDateRange
      );
      this.filterDraft.dateFrom = this.appliedFilters.dateFrom;
      this.filterDraft.dateTo = this.appliedFilters.dateTo;
      this.loadReportData();
    },
    selectPeriodPreset(presetId) {
      const preset = getReportPeriodPresetById(presetId);
      if (!preset) return;
      const [start, end] = preset.getRange();
      const dateFrom = toDateInputValue(start);
      const dateTo = toDateInputValue(end);
      this.filterDraft.dateFrom = dateFrom;
      this.filterDraft.dateTo = dateTo;
      this.appliedFilters = { dateFrom, dateTo };
      this.loadReportData();
    },
    clearFilters() {
      const range = defaultReportDateRange();
      const dateFrom = toDateInputValue(range[0]);
      const dateTo = toDateInputValue(range[1]);
      this.filterDraft.dateFrom = dateFrom;
      this.filterDraft.dateTo = dateTo;
      this.appliedFilters = { dateFrom, dateTo };
      this.loadReportData();
    },
    async loadReportData() {
      this.loading = true;
      try {
        const { dateFrom, dateTo } = this.appliedFilters;
        const result = await loadAgentReportStatistics(dateFrom, dateTo);
        this.summary = result.summary;
        this.byProduct = result.byProduct;
        this.byStatus = result.byStatus;
        this.chartRenderKey += 1;
      } catch (err) {
        this.summary = { salesCount: 0, premiumSum: 0, insuredCount: 0, productsUsedCount: 0 };
        this.byProduct = [];
        this.byStatus = [];
        this.chartRenderKey += 1;
        if (err?.response?.status === 401) {
          this.$router.push("/signIn/catalog");
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style src="@/assets/scss/hezabat.scss" lang="scss"></style>
<style src="@/assets/scss/report-page.scss" lang="scss"></style>
